// @flow

import {concat} from 'lodash';
import type {NodeChildren, NodeAttributes, Edge, Element, Node} from '../types';

const buildEntityDiagramNode = (
  attributes: NodeAttributes,
  children: NodeChildren,
): Node => {
  const {id, x, y} = attributes;
  // Each entity diagram node has two children
  // 1. a link or paragraph that's rendered as the header
  // 2. a table that's render as the attribute list
  const header = children[0];
  const table = children[1];
  const attrs = [];
  const handles = [];

  // Creating an object with the attribute table data (rather than rendering the <table> directly)
  // gives us the flexibility to add edge handles to specific table rows so we can draw an edge
  // from a row in a node to another node
  const parseAttributeValue = (child) => {
    if (typeof child !== 'string' && child.name === 'node') {
      // If the table cell contains a node then we need to create a
      // custom handle to draw edges to this specific row when we render the EntityDiagramNode
      handles.push(child.attributes.id);
      return {content: child.children[0], handle: child.attributes.id};
    } else {
      return {content: child};
    }
  };

  table.children[1].children.forEach((tr) => {
    const [td1, td2] = tr.children;

    // Get the name and value of the attribute from the table row
    const name = parseAttributeValue(td1.children[0]);
    const value = parseAttributeValue(td2.children[0]);
    const handle = name.handle || value.handle || null;
    attrs.push({name: name.content, value: value.content, handle});
  });

  return {
    id,
    type: 'entity',
    data: {
      header,
      attributes: attrs,
      handles,
    },
    position: {x, y},
  };
};

const buildEntityDiagramEdge = (
  attributes: NodeAttributes,
  children: NodeChildren,
  nodes: Array<Node>,
) => {
  const {from, to, fromPosition, toPosition, style} = attributes;
  const nodeIds = nodes.map((n) => n.id);
  const findParentNode = (id) => {
    return nodes.find((n) => n.data.handles.includes(id));
  };

  let source = from;
  let target = to;
  let sourceHandle;
  let targetHandle;

  if (!nodeIds.includes(from)) {
    // then we are drawing an edge from a a node within another node
    const parent = findParentNode(from);
    sourceHandle = from;
    source = parent?.id || from;
  }

  if (!nodeIds.includes(to)) {
    // then we are drawing an edge to a node within another node
    const parent = findParentNode(from);
    targetHandle = to;
    target = parent?.id || to;
  }

  const edge: Edge = {
    id: `edge-${from}-${to}-${fromPosition}-${toPosition}`,
    source,
    target,
    arrowHeadType: 'arrowclosed',
    type: style,
  };

  // If we are drawing an edge from/to a specific row in the node
  // add a source or targetHandle so react-flow-renderer knows where to draw the edge within the node
  if (sourceHandle) {
    edge.sourceHandle = sourceHandle;
  } else if (fromPosition !== 'auto') {
    edge.sourceHandle = fromPosition;
  }
  if (targetHandle) {
    edge.targetHandle = targetHandle;
  } else if (toPosition !== 'auto') {
    edge.targetHandle = toPosition;
  }
  return edge;
};

export const buildEntityDiagram = (
  attributes: NodeAttributes,
  children: NodeChildren | null,
): Array<Element> => {
  if (!children) {
    return [];
  }
  const nodeTags = children.filter((e) => e.name === 'node');
  const edgeTags = children.filter((e) => e.name === 'edge');

  const nodes = nodeTags.map((n) =>
    buildEntityDiagramNode(n.attributes, n.children),
  );

  const edges = edgeTags.map((e) => {
    return buildEntityDiagramEdge(e.attributes, e.children, nodes);
  });

  return concat(nodes, edges);
};
